import {
  Alert,
  Button,
  Group,
  List,
  PasswordInput,
  Progress,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconAlertTriangle, IconPlugConnected } from "@tabler/icons-react";
import { useState } from "react";
import { StatCard } from "@how/ui";
import {
  type NoteChoice,
  type PlanningCenterCredentials,
  type PlanningCenterPreparation,
  type PlanningCenterProgress,
  usePreparePlanningCenter,
  useRunPlanningCenterImport,
} from "./planning-center-queries";

const NOTE_CHOICES: readonly { value: NoteChoice; label: string }[] = [
  { value: "confidential", label: "Confidential notes" },
  { value: "ordinary", label: "Ordinary notes" },
  { value: "skip", label: "Do not import" },
];

/**
 * Importing from Planning Center (ADR 0044), in the order a church works through it: paste the token, see what would
 * come across, choose what happens to each category of notes, then run. The token stays in this component's state
 * for the run and is sent with each page; nothing stores it. The run stages rows into the same import the file path
 * uses, so the report, the commit and the duplicates work exactly as they do for a CSV.
 */
export function PlanningCenterImport({
  onStaged,
}: {
  readonly onStaged: (importId: string) => void;
}) {
  const [credentials, setCredentials] = useState<PlanningCenterCredentials>({
    appId: "",
    secret: "",
  });
  const [prepared, setPrepared] = useState<PlanningCenterPreparation | null>(null);
  const [choices, setChoices] = useState<Record<string, NoteChoice>>({});
  const [progress, setProgress] = useState<PlanningCenterProgress | null>(null);
  const prepare = usePreparePlanningCenter();
  const run = useRunPlanningCenterImport();

  const ready = credentials.appId.trim() !== "" && credentials.secret.trim() !== "";

  return (
    <Stack gap="md">
      <Title order={3}>Import people from Planning Center</Title>
      <Text size="sm" c="dimmed" maw={640}>
        In Planning Center, create a Personal Access Token (api.planningcenteronline.com, Personal
        Access Tokens) as someone who can see everyone you want to bring across. Paste its
        application id and secret here. It is used for this run only and is not kept anywhere.
      </Text>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" maw={720}>
        <TextInput
          label="Application id"
          autoComplete="off"
          value={credentials.appId}
          disabled={prepared !== null}
          onChange={(event) => {
            setCredentials({ ...credentials, appId: event.currentTarget.value });
          }}
        />
        <PasswordInput
          label="Secret"
          autoComplete="off"
          value={credentials.secret}
          disabled={prepared !== null}
          onChange={(event) => {
            setCredentials({ ...credentials, secret: event.currentTarget.value });
          }}
        />
      </SimpleGrid>
      {prepared === null ? (
        <Group>
          <Button
            leftSection={<IconPlugConnected size={16} />}
            disabled={!ready}
            loading={prepare.isPending}
            onClick={() => {
              prepare.mutate(credentials, {
                onSuccess: (result) => {
                  setPrepared(result);
                  setChoices(
                    Object.fromEntries(
                      result.noteCategories.map((c) => [c.id, "confidential" as const]),
                    ),
                  );
                },
              });
            }}
          >
            Connect and look
          </Button>
        </Group>
      ) : null}
      {prepare.isError ? (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
          {prepare.error.message}
        </Alert>
      ) : null}

      {prepared === null ? null : (
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <StatCard label="Planning Center account" value={prepared.organization.name} />
            <StatCard label="People" value={prepared.people.toLocaleString()} />
            <StatCard label="Note categories" value={String(prepared.noteCategories.length)} />
          </SimpleGrid>

          <Stack gap={4}>
            <Text size="sm" fw={600}>
              Campuses
            </Text>
            <Text size="sm">
              {prepared.campuses.matched.length === 0
                ? "None of their campuses match one of yours by name."
                : `Matched: ${prepared.campuses.matched.join(", ")}.`}{" "}
              {prepared.campuses.unmatched.length === 0
                ? null
                : `Not here yet: ${prepared.campuses.unmatched.join(", ")}. A person on one of those is flagged in the report; create the campus with the same name and run again.`}
            </Text>
          </Stack>

          <Stack gap={4}>
            <Text size="sm" fw={600}>
              Custom fields
            </Text>
            <Text size="sm">
              {prepared.fields.matched.length === 0
                ? "None of their fields match one of yours by name."
                : `Matched: ${prepared.fields.matched.join(", ")}.`}{" "}
              {prepared.fields.unmatched.length === 0
                ? null
                : `Not here yet: ${prepared.fields.unmatched.join(", ")}. A person with a value in one of those is flagged; create the field with the same label and run again, or leave it.`}
            </Text>
          </Stack>

          {prepared.noteCategories.length === 0 ? null : (
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Notes
              </Text>
              <Text size="sm" c="dimmed" maw={640}>
                A note in Planning Center is seen by the people its category allows. The nearest
                thing here is a confidential note, so that is the default; choose otherwise per
                category.
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="sm">
                {prepared.noteCategories.map((category) => (
                  <Select
                    key={category.id}
                    label={category.name}
                    data={NOTE_CHOICES}
                    allowDeselect={false}
                    value={choices[category.id] ?? "confidential"}
                    onChange={(value) => {
                      setChoices((current) => ({
                        ...current,
                        [category.id]: (value as NoteChoice | null) ?? "confidential",
                      }));
                    }}
                  />
                ))}
              </SimpleGrid>
            </Stack>
          )}

          <Stack gap={4}>
            <Text size="sm" fw={600}>
              What does not come across
            </Text>
            <List size="sm" spacing={2}>
              {prepared.notBrought.map((item) => (
                <List.Item key={item}>{item}</List.Item>
              ))}
            </List>
            <Text size="sm" c="dimmed">
              Emails and phone numbers come across, but not permission to message anyone: nobody
              agreed to that here. An address marked as blocked becomes "do not email".
            </Text>
          </Stack>

          {progress === null ? null : (
            <Stack gap={4} maw={420}>
              <Text size="sm">
                {progress.phase === "people" ? "Reading people" : "Reading notes"}:{" "}
                {progress.done.toLocaleString()}
                {progress.total > 0 ? ` of ${progress.total.toLocaleString()}` : ""}
              </Text>
              <Progress
                value={progress.total === 0 ? 0 : (progress.done / progress.total) * 100}
                aria-label="Records read"
              />
            </Stack>
          )}

          <Group>
            <Button
              loading={run.isPending}
              onClick={() => {
                setProgress(null);
                run.mutate(
                  {
                    credentials,
                    organizationName: prepared.organization.name,
                    people: prepared.people,
                    noteCategories: choices,
                    onProgress: setProgress,
                  },
                  {
                    onSuccess: (importId) => {
                      setCredentials({ appId: "", secret: "" });
                      setPrepared(null);
                      onStaged(importId);
                    },
                  },
                );
              }}
            >
              Read {prepared.people.toLocaleString()} people
            </Button>
            <Button
              variant="subtle"
              disabled={run.isPending}
              onClick={() => {
                setPrepared(null);
                setProgress(null);
              }}
            >
              Start over
            </Button>
          </Group>
          {run.isError ? (
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
              The run stopped. {run.error.message} You can read again: pages already read are kept,
              not doubled.
            </Alert>
          ) : null}
        </Stack>
      )}
    </Stack>
  );
}
